import { Button, ButtonVariants } from '@triumph/design-system';
import { Checkbox } from 'primereact/checkbox';
import { useEffect, useMemo, useState } from 'react';

import { useAddConditionNoteMutation } from '@/components/shared/api/mutations/conditions';
import { Dialog } from '@/components/shared/components/Dialog';
import { FormattedText } from '@/components/shared/components/FormattedText';
import { Linkify } from '@/components/shared/components/Linkify';
import {
    MentionTextarea,
    useMentionSuggestions,
} from '@/components/shared/components/MentionTextarea';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import {
    ConditionPickerLabel,
    ConditionPickerList,
    ConditionPickerRow,
    ErrorText,
    FormActions,
    FormLabel,
    ModalContainer,
    ModalField,
    ModalSubtitle,
    ProductPill,
    RequiredAsterisk,
    SelectAllLink,
} from './ApplicantConditions.StyledComponents';
import { Condition } from './ApplicantConditions.types';
import { getProductScopeLabel } from './ApplicantConditions.utils';

interface Props {
    applicantId: string;
    openConditions: Condition[];
    visible: boolean;
    onClose: () => void;
}

export const BulkAddNoteModal = ({ applicantId, openConditions, visible, onClose }: Props) => {
    const [note, setNote] = useState('');
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);

    const mentionSuggestions = useMentionSuggestions(applicantId);

    const { isConditionTextFormattingEnabled } = useFeatureFlags();

    const { mutateAsync: saveNote, isPending } = useAddConditionNoteMutation();

    useEffect(() => {
        if (visible) {
            setSelectedIds(new Set(openConditions.map((c) => c.id)));
            setError(null);
            setNote('');
        }
    }, [visible, openConditions]);

    const allSelected = useMemo(
        () => openConditions.length > 0 && selectedIds.size === openConditions.length,
        [openConditions, selectedIds],
    );

    const toggle = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const handleSelectAll = () => {
        setSelectedIds(new Set(openConditions.map((c) => c.id)));
    };

    const handleClose = () => {
        onClose();
    };

    const handleSave = async () => {
        const trimmed = note.trim();
        if (!trimmed) {
            setError('A note is required.');
            return;
        }
        if (selectedIds.size === 0) {
            setError('Select at least one task/condition.');
            return;
        }
        try {
            await Promise.all(
                Array.from(selectedIds).map((conditionId) =>
                    saveNote({
                        applicantId,
                        conditionId,
                        note: trimmed,
                        isResolutionNote: false,
                    }),
                ),
            );
            handleClose();
        } catch {
            setError('Failed to save one or more notes. Please try again.');
        }
    };

    return (
        <Dialog
            id="bulk-add-note-modal"
            header="Add Note to Tasks/Conditions"
            visible={visible}
            onHide={handleClose}
            dismissableMask={false}
            data-testid="bulk-add-note-dialog"
        >
            <ModalContainer>
                <ModalSubtitle>
                    Write a note and select which open tasks/conditions to add it to.
                </ModalSubtitle>

                <ModalField>
                    <FormLabel htmlFor="bulk-add-note-input">
                        Note<RequiredAsterisk>*</RequiredAsterisk>
                    </FormLabel>
                    <MentionTextarea
                        spellCheck
                        id="bulk-add-note-input"
                        data-testid="bulk-add-note-input"
                        placeholder="Describe progress, what you verified, or next steps…"
                        value={note}
                        onChange={(val) => {
                            setNote(val);
                            if (error) setError(null);
                        }}
                        hasError={Boolean(error)}
                        suggestions={mentionSuggestions}
                    />
                </ModalField>

                <ModalField>
                    <FormLabel>Apply to</FormLabel>
                    <SelectAllLink
                        type="button"
                        onClick={handleSelectAll}
                        disabled={allSelected || openConditions.length === 0}
                        data-testid="bulk-add-note-select-all"
                    >
                        Select all
                    </SelectAllLink>
                    <ConditionPickerList>
                        {openConditions.map((c) => {
                            const checked = selectedIds.has(c.id);
                            const scope = getProductScopeLabel(c.products);
                            return (
                                <ConditionPickerRow
                                    key={c.id}
                                    selected={checked}
                                    htmlFor={`bulk-pick-${c.id}`}
                                >
                                    <Checkbox
                                        inputId={`bulk-pick-${c.id}`}
                                        data-testid={`bulk-pick-${c.id}`}
                                        checked={checked}
                                        onChange={() => toggle(c.id)}
                                    />
                                    <ConditionPickerLabel
                                        $markdown={isConditionTextFormattingEnabled}
                                    >
                                        {isConditionTextFormattingEnabled ? (
                                            <FormattedText>{c.description}</FormattedText>
                                        ) : (
                                            <Linkify>{c.description}</Linkify>
                                        )}
                                    </ConditionPickerLabel>
                                    {scope ? <ProductPill>{scope}</ProductPill> : null}
                                </ConditionPickerRow>
                            );
                        })}
                    </ConditionPickerList>
                </ModalField>

                {error ? <ErrorText>{error}</ErrorText> : null}

                <FormActions style={{ justifyContent: 'flex-end' }}>
                    <Button
                        variant={ButtonVariants.secondary}
                        onClick={handleClose}
                        disabled={isPending}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={ButtonVariants.primary}
                        onClick={handleSave}
                        loading={isPending}
                        disabled={isPending || openConditions.length === 0}
                        data-testid="bulk-add-note-save"
                    >
                        Save Note
                    </Button>
                </FormActions>
            </ModalContainer>
        </Dialog>
    );
};
